import { gql } from "@apollo/client";

// The list page shows identity (MAC / license plate) plus ONLY the columns the
// POS heartbeat writes (see posapp deviceHeartbeat.js). Quickbase asset fields
// live on the device detail page.
export const GET_DEVICES = gql`
  query devices(
    $where: devices_bool_exp
    $orderBy: [devices_order_by!]
    $limit: Int!
    $offset: Int!
  ) {
    devices(where: $where, order_by: $orderBy, limit: $limit, offset: $offset) {
      id
      mac_address
      license_plate
      android_device_id
      updated_at
      active_event_id
      active_vendor_id
      session_order_count
      reported_android_id
    }
  }
`;

export const GET_DEVICES_COUNT = gql`
  query devicesCount($where: devices_bool_exp) {
    devices_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

// Devices currently assigned to a given event.
export const GET_DEVICES_BY_EVENT = gql`
  query devicesByEvent($eventId: Int!) {
    devices(
      where: { assigned_event_id: { _eq: $eventId } }
      order_by: { updated_at: desc }
    ) {
      id
      mac_address
      manufacturer
      model_number
      internal_part_number
      android_device_id
      license_plate
      deployment_status
      current_possession
      deployed_event_name
      deployed_at
      updated_at
    }
  }
`;

// Every device with any connection to the event: back-office assignment,
// POS-reported live session (active_event_id, from the heartbeat — the same
// signal /devices/list uses), or a license plate that appears on the event's
// orders. The three sets genuinely diverge in production — devices can sell
// without heartbeating and heartbeat without selling.
export const GET_EVENT_DEVICES_UNION = gql`
  query eventDevicesUnion($eventId: Int!, $plates: [String!]!) {
    devices(
      where: {
        _or: [
          { assigned_event_id: { _eq: $eventId } }
          { active_event_id: { _eq: $eventId } }
          { license_plate: { _in: $plates } }
        ]
      }
      order_by: { updated_at: desc }
    ) {
      id
      mac_address
      manufacturer
      model_number
      internal_part_number
      android_device_id
      license_plate
      deployment_status
      current_possession
      deployed_event_name
      deployed_at
      updated_at
      assigned_event_id
      active_event_id
      session_order_count
    }
  }
`;

// Roster of devices with no current event assignment, for the picker modal.
export const GET_UNASSIGNED_DEVICES = gql`
  query unassignedDevices($search: String, $limit: Int!) {
    devices(
      where: {
        assigned_event_id: { _is_null: true }
        _or: [
          { mac_address: { _ilike: $search } }
          { license_plate: { _ilike: $search } }
          { internal_part_number: { _ilike: $search } }
          { manufacturer: { _ilike: $search } }
          { model_number: { _ilike: $search } }
        ]
      }
      order_by: { mac_address: asc }
      limit: $limit
    ) {
      id
      mac_address
      manufacturer
      model_number
      internal_part_number
      license_plate
      current_possession
      updated_at
    }
  }
`;

// Single, event-scoped orders touch. Returns one row per device that has
// orders for this event (the most recent). Cheap because event_id is
// indexed on orders.
// Latest order per physical device at the event. orders.device_id is null in
// production — the working key is orders.android_device_id, which despite its
// name holds the device's LICENSE PLATE (e.g. "002976"). Date-bounded because
// event ids are reused across years and stale devices would pollute the list.
export const GET_EVENT_DEVICE_ORDERS = gql`
  query eventDeviceOrders($eventId: Int!, $from: timestamptz!, $to: timestamptz!) {
    orders(
      where: {
        event_id: { _eq: $eventId }
        created_at: { _gte: $from, _lt: $to }
      }
      distinct_on: [android_device_id]
      order_by: [{ android_device_id: asc }, { created_at: desc }]
    ) {
      android_device_id
      created_at
      transaction_at
      build_id
    }
  }
`;

// Just id + name, to resolve a device's active_vendor_id to a vendor name and
// group the list by vendor. No FK relationship exists on the column, so this is
// looked up client-side.
export const GET_VENDOR_NAMES = gql`
  query deviceVendorNames {
    vendors(order_by: { name: asc }) {
      id
      name
    }
  }
`;

export const GET_DEVICE_LATEST_ORDERS = gql`
  query deviceLatestOrders($deviceIds: [Int!]!) {
    orders(
      where: { device_id: { _in: $deviceIds } }
      distinct_on: [device_id]
      order_by: [{ device_id: asc }, { created_at: desc }]
    ) {
      device_id
      created_at
      transaction_at
    }
  }
`;

export const GET_DEVICE = gql`
  query devicesByPk($id: Int!) {
    devices_by_pk(id: $id) {
      id
      mac_address
      item_type
      created_at
      updated_at
    }
  }
`;

export const GET_DEVICE_DETAIL = gql`
  query deviceDetail($id: Int!, $limit: Int!) {
    devices_by_pk(id: $id) {
      id
      mac_address
      item_type
      created_at
      updated_at
      license_plate
      warehouse_desc
      item_status
      deployment_status
      current_possession
      deployed_event_name
      deployed_at
      item_summary_desc
      item_description
      manufacturer
      model_number
      internal_part_number
      android_device_id
      card_reader_serial
      card_reader_pin
      pcb_serial
      sim_imei
      replacement_cost_cents
      weight_lbs
      length_in
      width_in
      height_in
    }
    orders(
      where: { device_id: { _eq: $id } }
      order_by: { created_at: desc }
      limit: $limit
    ) {
      id
      reference_id
      subtotal
      status
      created_at
      transaction_at
      event {
        id
        name
      }
    }
  }
`;
